import React, { useState, useMemo, useEffect } from 'react';
import { Grid, Button, RadioGroup, FormControlLabel, Radio, FormControl, TextField, Checkbox, Typography, CircularProgress, Snackbar, Alert } from '@mui/material';
import { useQuery } from 'react-query';
import { apiService } from '../../../../core/services/apiService';
import { getWorkplaceEmployeesUsers } from 'core/API/broadcast';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import styles from '../SchedulerSetting.module.scss';
import broadcastStyles from './CreateBroadcastGroup.module.scss';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import { isHiddenSecurityRole } from '../GeneralSetting/constants';

interface BroadcastGroup {
	id: number;
	groupName: string;
	participants?: any[];
	securityRoles?: any[];
	workplace?: string | null;
}

interface CreateBroadcastGroupProps {
	backButtonClick: () => void;
	onBroadcastGroupCreated?: () => void;
	editingGroup?: BroadcastGroup | null;
}

// Shape from getWorkplaceEmployeesUsers (broadcast user API)
interface User {
	id: number;
	firstName: string;
	lastName: string;
	email?: string;
}

interface Role {
	id: number;
	name: string;
	description?: string;
}

const CreateBroadcastGroup: React.FC<CreateBroadcastGroupProps> = ({
	backButtonClick,
	onBroadcastGroupCreated,
	editingGroup = null,
}) => {
	const { workplaceId } = useWorkplacesContext();
	const isEditMode = !!editingGroup;
	// Add and Edit both use edit permission
	const { hasViewPermission, hasEditPermission } = usePermissionCheck();
	const canViewBroadcast = hasViewPermission('Broadcast');
	const canEditBroadcast = hasEditPermission('Broadcast');
	const canSubmit = canEditBroadcast;
	
	// Determine initial group type based on editing group
	const getInitialGroupType = (): 'User' | 'Role' => {
		if (editingGroup) {
			if (editingGroup.participants && editingGroup.participants.length > 0) {
				return 'User';
			}
			if (editingGroup.securityRoles && editingGroup.securityRoles.length > 0) {
				return 'Role';
			}
		}
		return 'User';
	};

	const [groupType, setGroupType] = useState<'User' | 'Role'>(getInitialGroupType());
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [groupName, setGroupName] = useState(editingGroup?.groupName || '');
	const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
	const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
	const [groupNameError, setGroupNameError] = useState<string | null>(null);
	const [selectionError, setSelectionError] = useState<string | null>(null);
	const [hasPrefilled, setHasPrefilled] = useState(false);
	const [snackbar, setSnackbar] = useState<{
		open: boolean;
		message: string;
		severity: 'success' | 'error' | 'info' | 'warning';
	}>({
		open: false,
		message: '',
		severity: 'success',
	});

	// Reset prefill flag when editingGroup changes
	useEffect(() => {
		if (editingGroup) {
			setHasPrefilled(false);
		}
	}, [editingGroup?.id]);

	// Determine what to fetch - if editing, fetch based on what exists in editingGroup
	const shouldFetchUsers = groupType === 'User' || (isEditMode && editingGroup?.participants && editingGroup.participants.length > 0);
	const shouldFetchRoles = groupType === 'Role' || (isEditMode && editingGroup?.securityRoles && editingGroup.securityRoles.length > 0);

	// Fetch users via broadcast user API (same as Send Message / To dropdown)
	const { data: usersData, isLoading: isLoadingUsers } = useQuery<User[]>(
		['workplace-employees-users', workplaceId],
		() => getWorkplaceEmployeesUsers(workplaceId),
		{
			enabled: shouldFetchUsers && !!workplaceId,
		}
	);

	// Fetch roles
	const { data: rolesData, isLoading: isLoadingRoles } = useQuery<{ member: Role[] }>(
		['broadcastRoles', workplaceId],
		async () => {
			const response = apiService.responseHandler(
				await apiService.get('/broadcast_groups/roles', {
					params: {
						workplace: workplaceId || 1,
						page: 1,
						itemsPerPage: 100,
						pagination: false,
					},
				})
			);
			return response || { member: [] };
		},
		{
			enabled: shouldFetchRoles && !!workplaceId,
		}
	);

	const users = usersData || [];
	const roles = rolesData?.member || [];

	// Use users in API order (no frontend sorting)
	const filteredUsers = users;
	
	// Filter out system/admin roles hidden from user-visible lists
	const filteredRoles = roles.filter((role: Role) => !isHiddenSecurityRole(role.name));

	// Prefill selected users/roles when editing (run after users/roles are loaded)
	useEffect(() => {
		if (editingGroup && isEditMode && !hasPrefilled) {
			// Extract user IDs from participants
			if (editingGroup.participants && editingGroup.participants.length > 0 && users.length > 0) {
				const userIds = editingGroup.participants
					.map((p: any) => {
						if (typeof p === 'object' && p.id) {
							return p.id;
						}
						if (typeof p === 'string' && p.includes('/users/')) {
							const match = p.match(/\/users\/(\d+)/);
							return match ? parseInt(match[1]) : null;
						}
						return null;
					})
					.filter((id: number | null): id is number => id !== null);
				
				if (userIds.length > 0) {
					setSelectedUsers(userIds);
					setGroupType('User');
					setHasPrefilled(true);
				}
			}

			// Extract role IDs from securityRoles
			if (editingGroup.securityRoles && editingGroup.securityRoles.length > 0 && roles.length > 0) {
				const roleIds = editingGroup.securityRoles
					.map((r: any) => {
						if (typeof r === 'object' && r.id) {
							return r.id;
						}
						if (typeof r === 'string' && r.includes('/security_roles/')) {
							const match = r.match(/\/security_roles\/(\d+)/);
							return match ? parseInt(match[1]) : null;
						}
						return null;
					})
					.filter((id: number | null): id is number => id !== null);
				
				if (roleIds.length > 0) {
					setSelectedRoles(roleIds);
					setGroupType('Role');
					setHasPrefilled(true);
				}
			}
		}
	}, [editingGroup, isEditMode, users, roles, hasPrefilled]);

	// Check if all filtered items are selected
	const isAllUsersSelected = useMemo(() => {
		return filteredUsers.length > 0 && filteredUsers.every((user) => selectedUsers.includes(user.id));
	}, [filteredUsers, selectedUsers]);

	const isAllRolesSelected = useMemo(() => {
		return filteredRoles.length > 0 && filteredRoles.every((role) => selectedRoles.includes(role.id));
	}, [filteredRoles, selectedRoles]);

	// Handle "Select all" for users
	const handleSelectAllUsers = () => {
		if (!canSubmit) return;
		if (isAllUsersSelected) {
			setSelectedUsers((prev) => prev.filter((id) => !filteredUsers.some((user) => user.id === id)));
		} else {
			const newSelections = Array.from(new Set([...selectedUsers, ...filteredUsers.map((user) => user.id)]));
			setSelectedUsers(newSelections);
		}
	};

	// Handle "Select all" for roles
	const handleSelectAllRoles = () => {
		if (!canSubmit) return;
		if (isAllRolesSelected) {
			setSelectedRoles((prev) => prev.filter((id) => !filteredRoles.some((role) => role.id === id)));
		} else {
			const newSelections = Array.from(new Set([...selectedRoles, ...filteredRoles.map((role) => role.id)]));
			setSelectedRoles(newSelections);
		}
	};

	// Handle individual user checkbox toggle
	const handleUserToggle = (userId: number) => {
		if (!canSubmit) return;
		setSelectedUsers((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
	};

	// Handle individual role checkbox toggle
	const handleRoleToggle = (roleId: number) => {
		if (!canSubmit) return;
		setSelectedRoles((prev) => (prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]));
	};

	// Reset selections when switching between User and Role (only in create mode)
	useEffect(() => {
		if (!isEditMode && !hasPrefilled) {
			setSelectedUsers([]);
			setSelectedRoles([]);
		}
	}, [groupType, isEditMode, hasPrefilled]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!canSubmit) {
			setError('You do not have permissions to save broadcast groups.');
			return;
		}

		// Reset errors
		setGroupNameError(null);
		setSelectionError(null);
		setError(null);

		// Validate group name
		if (!groupName.trim()) {
			setGroupNameError('Group name is required');
			return;
		}

		// Validate at least one checkbox is selected
		const hasSelection = groupType === 'User' ? selectedUsers.length > 0 : selectedRoles.length > 0;
		if (!hasSelection) {
			setSelectionError(`Please select at least one ${groupType === 'User' ? 'user' : 'role'}`);
			return;
		}

		setIsSubmitting(true);

		try {
			// Save against the same workplace the users/roles lists were loaded for
			const groupWorkplaceId = workplaceId || Number(localStorage.getItem('workplace-id')) || 1;

			// Prepare participants (users) or securityRoles based on selection
			const participants = groupType === 'User'
				? selectedUsers.map((userId) => `/api/users/${userId}`)
				: [];

			const securityRoles = groupType === 'Role'
				? selectedRoles.map((roleId) => `/api/security_roles/${roleId}`)
				: [];

			const payload = {
				groupName: groupName.trim(),
				participants: participants,
				securityRoles: securityRoles,
				workplace: `/api/workplaces/${groupWorkplaceId}`,
			};

			if (isEditMode && editingGroup) {
				// Update existing group
				await apiService.put(`/broadcast_groups/${editingGroup.id}`, payload);
				setSnackbar({
					open: true,
					message: 'Broadcast group updated successfully',
					severity: 'success',
				});
			} else {
				// Create new group
				await apiService.post('/broadcast_groups', payload);
				setSnackbar({
					open: true,
					message: 'Broadcast group created successfully',
					severity: 'success',
				});
			}
			
			onBroadcastGroupCreated?.();

			// Navigate back after a short delay
			setTimeout(() => {
				backButtonClick();
			}, 1500);
		} catch (err: any) {
			const errorMessage = err?.message || `Failed to ${isEditMode ? 'update' : 'create'} broadcast group`;
			setError(errorMessage);
			setSnackbar({
				open: true,
				message: errorMessage,
				severity: 'error',
			});
			setIsSubmitting(false);
		}
	};

	const isLoading = groupType === 'User' ? isLoadingUsers : isLoadingRoles;
	const currentItems = groupType === 'User' ? filteredUsers : filteredRoles;
	const selectedItems = groupType === 'User' ? selectedUsers : selectedRoles;
	const isAllSelected = groupType === 'User' ? isAllUsersSelected : isAllRolesSelected;
	const handleSelectAll = groupType === 'User' ? handleSelectAllUsers : handleSelectAllRoles;
	const handleToggle = groupType === 'User' ? handleUserToggle : handleRoleToggle;

	return (
		<div className={styles.createTimeOffReason}>
			{!canViewBroadcast ? (
				<div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
					You do not have permissions to view Broadcast groups.
				</div>
			) : (
			<>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={backButtonClick}>
						Back
					</Button>
					<Button className={styles.submitButton} onClick={handleSubmit} disabled={isSubmitting || !canSubmit}>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>
						{isEditMode ? 'Edit Broadcast Group' : 'Create New Broadcast Group'}
					</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				{/* Group Name Input */}
				<TextField
					label="Group Name"
					name="groupName"
					placeholder="Enter group name"
					value={groupName}
					onChange={(e) => {
						setGroupName(e.target.value);
						if (groupNameError) setGroupNameError(null);
					}}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					error={!!groupNameError}
					helperText={groupNameError}
					style={{ marginBottom: 16 }}
					disabled={!canSubmit}
				/>

				<FormControl component="fieldset" fullWidth style={{ marginBottom: 24 }}>
					<RadioGroup
						row
						aria-label="group-type"
						name="group-type"
						value={groupType}
						onChange={(e) => setGroupType(e.target.value as 'User' | 'Role')}
					>
						<FormControlLabel
							value="User"
							control={<Radio sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }} />}
							label="User"
							disabled={!canSubmit}
						/>
						<FormControlLabel
							value="Role"
							control={<Radio sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }} />}
							label="Role"
							disabled={!canSubmit}
						/>
					</RadioGroup>
				</FormControl>

				{/* Loading State */}
				{isLoading && (
					<div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
						<CircularProgress size={24} />
					</div>
				)}

				{!isLoading && (
					<>
						{/* Select All Checkbox */}
						{currentItems.length > 0 && (
							<div className={broadcastStyles.selectAll}>
								<FormControlLabel
									control={
										<Checkbox
											style={{ color: 'rgb(68, 99, 136)' }}
											checked={isAllSelected}
											onChange={(e) => {
												handleSelectAll();
												if (selectionError) setSelectionError(null);
											}}
											disabled={!canSubmit}
										/>
									}
									label="Select All"
								/>
							</div>
						)}

						{/* Selection Error Message */}
						{selectionError && (
							<Typography variant="body2" color="error" style={{ marginBottom: 8, marginLeft: 14 }}>
								{selectionError}
							</Typography>
						)}

						{/* Items List */}
						{currentItems.length === 0 ? (
							<Typography className={broadcastStyles.noItemsText} variant="body2">
								{`No ${groupType === 'User' ? 'users' : 'roles'} available.`}
							</Typography>
						) : (
							<div className={broadcastStyles.checkboxGrid}>
								<Grid container spacing={1}>
									{currentItems.map((item) => {
										const itemId = item.id;
										const displayName =
											groupType === 'User'
												? `${(item as User).firstName || ''} ${(item as User).lastName || ''}`.trim()
												: (item as Role).name;
										const isSelected = selectedItems.includes(itemId);

										return (
											<Grid item xs={12} sm={6} md={4} lg={3} key={itemId}>
												<div className={broadcastStyles.checkboxItem}>
													<Checkbox
														style={{ color: 'rgb(68, 99, 136)' }}
														checked={isSelected}
														onChange={() => {
															handleToggle(itemId);
															if (selectionError) setSelectionError(null);
														}}
														disabled={!canSubmit}
													/>
													<span className={broadcastStyles.itemLabel}>{displayName}</span>
												</div>
											</Grid>
										);
									})}
								</Grid>
							</div>
						)}
					</>
				)}
			</form>

			{/* Snackbar for success/error messages */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
			</>
			)}
		</div>
	);
};

export default CreateBroadcastGroup;

